import { useEffect, useState } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { githubAuthApiRef } from '../../apis';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import GitHubIcon from '@material-ui/icons/GitHub';
import { kyndrylColors } from '../../theme/kyndrylTheme';

/**
 * Props match what Backstage passes to the SignInPage component slot.
 * The critical contract: call `onSignInSuccess` with an IdentityApi
 * constructed via UserIdentity.create (or createGuest for guest).
 */
type KyndrylSignInPageProps = {
  onSignInSuccess: (identityApi: ReturnType<typeof UserIdentity.create>) => void;
};

const useStyles = makeStyles(() => ({
  root: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(160deg, ${kyndrylColors.deepForest} 0%, #1a1a2e 50%, ${kyndrylColors.deepForest} 100%)`,
  },
  card: {
    width: 400,
    maxWidth: '90vw',
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.06)',
    backdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.08)',
    padding: '48px 40px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 8,
  },
  wordmark: {
    width: 160,
    marginBottom: 16,
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: 500,
    color: '#fff',
    letterSpacing: '0.02em',
    textAlign: 'center' as const,
    marginBottom: 4,
  },
  tagline: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    marginBottom: 32,
  },
  githubButton: {
    width: '100%',
    padding: '12px 24px',
    borderRadius: 8,
    backgroundColor: kyndrylColors.springGreen,
    color: kyndrylColors.deepForest,
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none' as const,
    '&:hover': {
      backgroundColor: '#24c768',
    },
    '&:disabled': {
      backgroundColor: 'rgba(76, 221, 132, 0.4)',
      color: 'rgba(0,0,0,0.5)',
    },
  },
  guestLink: {
    marginTop: 16,
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
    '&:hover': {
      color: 'rgba(255,255,255,0.7)',
    },
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '0.8rem',
    marginTop: 8,
    textAlign: 'center' as const,
  },
}));

// Kyndryl wordmark SVG (white text on transparent)
const KyndrylWordmark = () => (
  <svg viewBox="0 0 600 120" xmlns="http://www.w3.org/2000/svg" style={{ width: 160 }}>
    <text
      x="300"
      y="85"
      textAnchor="middle"
      fill="#ffffff"
      fontFamily="'TWK Everett', 'Inter', sans-serif"
      fontWeight="500"
      fontSize="80"
      letterSpacing="2"
    >
      Kyndryl
    </text>
  </svg>
);

export const KyndrylSignInPage = ({ onSignInSuccess }: KyndrylSignInPageProps) => {
  const classes = useStyles();
  const githubAuth = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const [busy, setBusy] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount, check for an existing GitHub session (returning user / theme switch)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const identityResponse = await githubAuth.getBackstageIdentity({
          optional: true,
        } as any);
        if (cancelled) return;
        if (identityResponse) {
          const identityApi = UserIdentity.create({
            identity: identityResponse.identity,
            authApi: githubAuth,
            profile: await githubAuth.getProfile(),
          });
          onSignInSuccess(identityApi);
          return;
        }
      } catch {
        // No existing session — fall through to login page
      }
      if (!cancelled) setShowLogin(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While checking existing session, show a loading spinner
  if (!showLogin) {
    return (
      <Box className={classes.root}>
        <CircularProgress style={{ color: kyndrylColors.springGreen }} />
      </Box>
    );
  }

  const handleGitHubSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      // Trigger the OAuth popup — this gets the user authenticated
      await githubAuth.signIn();

      // Build the IdentityApi from the auth session
      const identityResponse = await githubAuth.getBackstageIdentity();
      if (!identityResponse) {
        throw new Error('No identity returned from GitHub auth');
      }
      const identityApi = UserIdentity.create({
        identity: identityResponse.identity,
        authApi: githubAuth,
        profile: await githubAuth.getProfile(),
      });
      onSignInSuccess(identityApi);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(message);
      setBusy(false);
    }
  };

  const handleGuestSignIn = () => {
    onSignInSuccess(UserIdentity.createGuest());
  };

  // Hide guest option in production
  const allowGuest = configApi.getOptionalString('auth.environment') !== 'production';

  return (
    <Box className={classes.root}>
      <Box className={classes.card}>
        <KyndrylWordmark />
        <Typography className={classes.title}>
          Internal Developer Platform
        </Typography>
        <Typography className={classes.tagline}>
          Build · Deploy · Observe
        </Typography>

        <Button
          className={classes.githubButton}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <GitHubIcon />}
          onClick={handleGitHubSignIn}
          disabled={busy}
          variant="contained"
          disableElevation
        >
          {busy ? 'Signing in…' : 'Continue with GitHub'}
        </Button>

        {error && (
          <Typography className={classes.errorText}>{error}</Typography>
        )}

        {allowGuest && (
          <button
            className={classes.guestLink}
            onClick={handleGuestSignIn}
            type="button"
          >
            Continue as Guest
          </button>
        )}
      </Box>
    </Box>
  );
};
